import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import {
  ModalRoot,
  ModalBackdrop,
  ModalPositioner,
  ModalContent,
  ModalTitle,
  FallbackImage,
} from '@rcw/ui';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'i18n/LocaleContext';
import MetaDecorator from 'components/layout/MetaDecorator';
import { jwtDecode } from 'jwt-decode';
import {
  TIER_DESCRIPTIONS,
  RVS_RULE_TYPE,
  INACTIVE_SUBSCRIPTION_STATUSES,
  HIDDEN_SUBSCRIPTION_STATUSES,
  RVS_SUBSCRIPTION_TYPE,
} from 'libs/constants';
import { hasRewardId } from 'controls/utils';
import { showError } from 'controls/modals';
import {
  useGetUserSubscriptionsQuery,
  useGetAllProductsQuery,
  useGetUserPaymentHistoryQuery,
  useGetAccountRuleStatusQuery,
  useGetProductsImagesQuery,
  useLazyGetPaymentInformationUrlQuery,
} from 'app/api/gatewayApiSlice';
import { SubscriptionCancellationDialog } from './SubscriptionCancellationDialog';
import { RewardSelectionDialog } from './RewardSelectionDialog';
import { RewardRemovalDialog } from './RewardRemovalDialog';

// Rewards are selected by `ruleType`, never by `source`. `source` is a free-form
// label the backend writes (e.g. 'Promo', 'Loyalty Reward') and new values appear
// without warning; `ruleType` is a closed set. Mirrors the legacy LoyaltyProgram
// component, which shows Loyalty-family rules unconditionally and Instant-family
// rules only once they are fulfilled and perpetual.
const isInstantReward = (rule) => (
  (rule?.ruleType === RVS_RULE_TYPE.INSTANT_REWARD
    || rule?.ruleType === RVS_RULE_TYPE.INSTANT_USER_PRODUCT)
  && Boolean(rule?.ruleStatus?.fulfilled)
  && Boolean(rule?.ruleStatus?.isPerpetual)
);

const isLoyaltyReward = (rule) => (
  rule?.ruleType === RVS_RULE_TYPE.LOYALTY_REWARD
  || rule?.ruleType === RVS_RULE_TYPE.REWARD
);

const isVisibleReward = (rule) => isInstantReward(rule) || isLoyaltyReward(rule);

// Reduce a rule to what identifies it in a claim: the source to attribute the
// claim to, plus whichever ids the backend actually supplied. Ids are resolved
// to strings here, and omitted entirely when absent, so the API layer can send
// what it receives without having to validate it.
const toClaimTarget = (rule) => {
  const status = rule?.ruleStatus || {};
  const target = { source: rule?.source ?? status.source };

  if (hasRewardId(status.userRuleId)) {
    target.userRuleId = String(status.userRuleId);
  }
  if (hasRewardId(status.userSubscriptionRuleId)) {
    target.userSubscriptionRuleId = String(status.userSubscriptionRuleId);
  }

  return target;
};

function InfoIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' fill='none'>
      <g
        stroke='#39a9f7'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
      >
        <path d='M13 24.636c6.426 0 11.636-5.21 11.636-11.636S19.426 1.364 13 1.364 1.364 6.574 1.364 13 6.574 24.636 13 24.636M13 8.346V13M13 17.654h.011' />
      </g>
      <defs>
        <clipPath id='a'>
          <path fill='#fff' d='M0 0h26v26H0z' />
        </clipPath>
      </defs>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'>
      <path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10M15 9l-6 6M9 9l6 6' />
    </svg>
  );
}

function SubscriptionCard({ sub, idToken, onCancel }) {
  const { t, locale } = useTranslation();
  const dispatch = useDispatch();
  // A nonrenew subscription keeps its `dateNextCharge`, but it will not be
  // charged again — the date is the last day of access. Labelling it "Next
  // Charge" tells a member who already cancelled that they are about to be
  // billed. Cancelled and expired subscriptions never reach this card.
  const isNonRenew = sub.statusDescription === 'nonrenew';
  const [fetchPaymentUrl, { isFetching: isLoadingPaymentUrl }] = useLazyGetPaymentInformationUrlQuery();
  // Subscriptions granted without a purchase (Admin Tool, redeemed keys) have no
  // registered payment and 409 here; there's no field on the subscription itself
  // that says so upfront, so the first click is what tells us. Once it 409s we
  // show why and disable the button rather than let the user hit the same dead
  // end again.
  const [paymentInfoUnavailable, setPaymentInfoUnavailable] = useState(false);

  const showPaymentInfoUnavailable = () => {
    setPaymentInfoUnavailable(true);
    dispatch(showError({
      header: 'Payment Information Unavailable',
      content: 'This subscription has no payment information to display.',
    }));
  };

  const handlePaymentInformation = async () => {
    if (!sub?.subscriptionId || !idToken) return;

    try {
      const res = await fetchPaymentUrl({ subscriptionId: sub.subscriptionId, idToken }).unwrap();
      if (res?.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      } else {
        showPaymentInfoUnavailable();
      }
    } catch (err) {
      showPaymentInfoUnavailable();
      console.error('[SubscriptionCard] Error fetching payment URL:', err);
    }
  };

  return (
    <div className='rounded-2xl border-black/50 border overflow-hidden'>
      <h3 className='bg-roland-blue px-8 py-4 text-white font-bold'>{ sub.tierDescription || sub.offer?.description || 'Subscription' }</h3>
      <div className='p-8 space-y-2'>
        <div>
          <span>
            <strong>
              {t('RVS.COMPONENT.SUBSCRIPTIONS.STATUS')}
              :
              {' '}
            </strong>
          </span>
          <span className='capitalize'>{sub.statusDescription}</span>
        </div>
        {sub.createdDate && (
          <div>
            <span>
              <strong>
                {t('RVS.COMPONENT.SUBSCRIPTIONS.CREATED_DATE')}
                :
                {' '}
              </strong>
            </span>
            <span>
              {new Date(sub.createdDate).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        )}
        {sub.dateNextCharge && (
          <div>
            <span>
              <strong>
                {isNonRenew
                  ? t('RVS.COMPONENT.SUBSCRIPTIONS.END_DATE')
                  : t('RVS.COMPONENT.SUBSCRIPTIONS.NEXT_CHARGE')}
                :
                {' '}
              </strong>
            </span>
            <span>
              {new Date(sub.dateNextCharge).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        )}
        <div className='flex flex-col md:flex-row gap-2 md:gap-4 pt-4 mb-2'>
          <button
            type='button'
            className={`btn-secondary text-[16px] md:text-sm flex items-center justify-center${(isLoadingPaymentUrl || paymentInfoUnavailable) ? ' opacity-50 cursor-not-allowed' : ''}`}
            disabled={isLoadingPaymentUrl || paymentInfoUnavailable}
            onClick={handlePaymentInformation}
            aria-label={t('RVS.COMPONENT.SUBSCRIPTIONS.BUTTON.PAYMENT_INFORMATION')}
          >
            {isLoadingPaymentUrl ? (
              <span className='w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin' />
            ) : (
              t('RVS.COMPONENT.SUBSCRIPTIONS.BUTTON.PAYMENT_INFORMATION')
            )}
          </button>
          {!INACTIVE_SUBSCRIPTION_STATUSES.includes(sub.statusDescription) && sub.subscriptionTypeId !== RVS_SUBSCRIPTION_TYPE.FREE && (
            <button
              type='button'
              className='btn-destructive'
              onClick={onCancel}
              aria-label={t('RVS.COMPONENT.SUBSCRIPTIONS.BUTTON.CANCEL_SUBSCRIPTION')}
            >
              {t('RVS.COMPONENT.SUBSCRIPTIONS.BUTTON.CANCEL_SUBSCRIPTION')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

SubscriptionCard.propTypes = {
  sub: PropTypes.shape({
    subscriptionId: PropTypes.number,
    subscriptionTypeId: PropTypes.number,
    tierDescription: PropTypes.string,
    statusDescription: PropTypes.string,
    dateNextCharge: PropTypes.string,
    createdDate: PropTypes.string,
    offer: PropTypes.shape({ description: PropTypes.string }),
  }).isRequired,
  idToken: PropTypes.string,
  onCancel: PropTypes.func,
};

export function AccountDetailPage() {
  const { user, isLoading: authLoading, isSigningOut } = useAuth();
  const userInfo = useSelector((state) => state.user.userInfo);
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const [showBilling, setShowBilling] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [productToRemove, setProductToRemove] = useState(null);

  const getUserId = (idToken) => {
    try {
      const payload = jwtDecode(idToken);
      return payload['custom:userId'] || null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    if (!authLoading && !user && !isSigningOut) {
      navigate('/login', { state: { returnUrl: window.location.pathname } });
    }
  }, [authLoading, user, isSigningOut, navigate]);

  const idToken = user?.id_token ?? null;
  const userId = idToken ? getUserId(idToken) : null;
  const username = userInfo?.email || '';
  // Deferred until userInfo is populated (App.js's effect, which runs after this
  // component's) so prepareHeaders always has a token to attach by the time these
  // fire, and a token renewal (new `user` reference, same userId/username) never
  // changes these queries' cache keys — no refetch storm.
  const skip = authLoading || !user || !userInfo;

  const {
    data: subsRes, isLoading: subsLoading, refetch: refetchSubs,
  } = useGetUserSubscriptionsQuery(undefined, { skip });
  const {
    data: allProdsRes, isLoading: productsLoading, refetch: refetchProducts,
  } = useGetAllProductsQuery(undefined, { skip });
  const {
    data: billingRes, isLoading: billingLoading, refetch: refetchBilling,
  } = useGetUserPaymentHistoryQuery(userId, { skip: skip || !userId });
  const {
    data: ruleRes, isLoading: ruleLoading, refetch: refetchRule,
  } = useGetAccountRuleStatusQuery(username, { skip: skip || !username });
  const {
    data: imagesRes, isLoading: imagesLoading, refetch: refetchImages,
  } = useGetProductsImagesQuery(undefined, { skip });

  const loading = subsLoading || productsLoading || billingLoading || ruleLoading || imagesLoading;
  const subscriptions = subsRes?.isSuccess ? (subsRes.data || []) : [];
  const allProducts = allProdsRes?.isSuccess ? (allProdsRes.data || []) : [];
  const billingHistory = Array.isArray(billingRes) ? billingRes : [];
  const userRuleStatus = ruleRes?.isSuccess ? (ruleRes.data || null) : null;
  const productImages = imagesRes?.isSuccess ? (imagesRes.data?.content?.images || []) : [];

  const refetchAll = () => {
    refetchSubs();
    refetchProducts();
    refetchBilling();
    refetchRule();
    refetchImages();
  };

  const handleViewBilling = () => {
    setShowBilling(true);
  };

  // The section lists what the user currently has, so ended subscriptions are
  // dropped regardless of type, matching the legacy page. Previously only
  // granted (Free) ones were hidden, which left purchased subscriptions on the
  // page after cancellation. Past subscriptions remain in Billing History.
  // Free subscriptions stay restricted to 'subscribed' (RCP-13195): they are
  // granted rather than bought, so any other status means they are not in force.
  const visibleSubscriptions = subscriptions.filter((s) => {
    if (s.tierDescription === TIER_DESCRIPTIONS.ENTRY) return false;
    if (HIDDEN_SUBSCRIPTION_STATUSES.includes(s.statusDescription)) return false;
    if (s.subscriptionTypeId === RVS_SUBSCRIPTION_TYPE.FREE) {
      return s.statusDescription === 'subscribed';
    }
    return true;
  });

  const visibleRules = Array.isArray(userRuleStatus)
    ? userRuleStatus.filter(isVisibleReward)
    : [];

  const metaDecorator = (
    <MetaDecorator
      title={t('RVS.COMPONENT.ACCOUNT_DETAIL.TITLE', 'Account Detail')}
      description={t('RVS.COMPONENT.ACCOUNT_DETAIL.TITLE', 'Account Detail')}
      link='account-detail'
      addPostfixTitle
      noIndex
    />
  );

  if (authLoading || !user) {
    return (
      <div className='flex-1 max-w-[860px] w-full mx-auto py-10 px-6'>
        {metaDecorator}
        <div className='text-center text-gray-500 py-12'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.LOADING', 'Loading...')}</div>
      </div>
    );
  }

  return (
    <>
      {metaDecorator}
      <div className='flex-1 max-w-[860px] w-full mx-auto py-10 px-6'>
        <h1 className='text-[26px] md:text-5xl leading-[30px] font-bold text-center py-10'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.TITLE')}</h1>

        {/* Subscriptions */}
        <h2 className='text-lg md:text-[26px] leading-[44px] font-bold'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.SUB_TITLE_01')}</h2>
        {loading ? (
          <div className='text-center text-gray-500 py-12'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.LOADING', 'Loading...')}</div>
        ) : visibleSubscriptions.length === 0 ? (
          <div className='rounded-2xl border-black/50 border flex flex-col md:flex-row md:items-center gap-4 p-4'>
            <div className='flex items-center gap-4 flex-grow'>
              <span className='flex-shrink-0'>
                <InfoIcon />
              </span>
              <p>{t('RVS.COMPONENT.SUBSCRIPTIONS.ENTRY_SUBSCRIPTION_TITLE')}</p>
            </div>
            <a
              href={process.env.REACT_APP_MEMBERSHIPS_PAGE}
              className='btn-primary md:px-8'
            >
              {t('RVS.COMPONENT.SUBSCRIPTIONS.BUTTON.NO_SUBSCRIPTIONS', 'Join Team Roland Cloud')}
            </a>
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            {
              visibleSubscriptions
                .map((sub) => (
                  <SubscriptionCard
                    key={sub.subscriptionId}
                    sub={sub}
                    idToken={idToken}
                    onCancel={() => {
                      setSelectedSub(sub);
                      setShowCancelDialog(true);
                    }}
                  />
                ))
            }
          </div>
        )}

        {/* Billing History */}
        <h2 className='text-lg md:text-[26px] leading-[44px] font-bold'>{t('RVS.COMPONENT.BILLING_HISTORY.TITLE')}</h2>
        {loading ? (
          <div className='text-center text-gray-500 py-4'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.LOADING', 'Loading...')}</div>
        ) : billingHistory.length === 0 ? (
          <div className='rounded-2xl border-black/50 border overflow-hidden flex items-center gap-4 p-4'>
            <span className='flex-shrink-0'>
              <InfoIcon />
            </span>
            <p className='flex-grow'>{t('RVS.COMPONENT.BILLING_HISTORY.NO_BILLING_HISTORY')}</p>
          </div>
        ) : (
          <button
            type='button'
            className='btn-secondary md:px-8'
            onClick={handleViewBilling}
            aria-label={t('RVS.COMPONENT.BILLING_HISTORY.BUTTON.VIEW')}
          >
            {t('RVS.COMPONENT.BILLING_HISTORY.BUTTON.VIEW')}
          </button>
        )}

        {/* Loyalty Program */}
        <h2 className='text-lg md:text-[26px] leading-[44px] font-bold'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.SUB_TITLE_02')}</h2>
        {loading ? (
          <div className='text-center text-gray-500 py-12'>{t('RVS.COMPONENT.ACCOUNT_DETAIL.LOADING', 'Loading...')}</div>
        ) : visibleRules.length > 0 ? (
          <div className='flex flex-col gap-4'>
            {visibleRules
              .map((rule, idx) => {
                const status = rule.ruleStatus || {};
                const available = status.eligible || 0;
                const selectedProducts = status.selectedProducts || [];

                return (
                  <div key={rule.ruleId || idx} className='rounded-2xl border-black/50 border overflow-hidden'>
                    <h3 className='bg-roland-blue px-8 py-4 text-white font-bold'>
                      {rule.ruleName || t('RVS.COMPONENT.LOYALTY_PROGRAM.PLAY_TWO_FOUR_LIFE')}
                    </h3>
                    <div className='p-8 space-y-4'>
                      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
                        <div>
                          <p className='font-bold'>
                            {t('RVS.COMPONENT.LOYALTY_PROGRAM.REWARDS_STATUS', 'Rewards Status')}
                            :
                          </p>
                          <p>
                            {t('RVS.COMPONENT.LOYALTY_PROGRAM.INSTRUMENT_AVAILABLE_PREFACE', 'You have ')}
                            <strong>
                              {' '}
                              {available}
                            </strong>
                            {available === 1
                              ? t('RVS.COMPONENT.LOYALTY_PROGRAM.INSTRUMENT_AVAILABLE_CLOSING', ' instrument available')
                              : t('RVS.COMPONENT.LOYALTY_PROGRAM.INSTRUMENTS_AVAILABLE_CLOSING', ' instruments available')}
                          </p>
                        </div>

                        {available > 0 && (
                          <button
                            type='button'
                            className='btn-primary px-8'
                            onClick={() => {
                              setSelectedRule(toClaimTarget(rule));
                              setShowClaimDialog(true);
                            }}
                            aria-label={t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CLAIM', 'Claim your instruments')}
                          >
                            {t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CLAIM', 'Claim your instruments')}
                          </button>
                        )}
                      </div>

                      {selectedProducts.length > 0 && (
                        <div className='pt-4 border-t border-black/10'>
                          <p className='font-bold mb-2'>
                            {t('RVS.COMPONENT.LOYALTY_PROGRAM.REWARDS_CURRENT_INSTRUMENTS', 'Current Rewards Instruments')}
                            :
                          </p>
                          <ul className='list-inside space-y-2'>
                            {selectedProducts.map((item) => {
                              const pId = item.productId || item;
                              const product = allProducts.find((p) => String(p.id) === String(pId));
                              const productImage = productImages.find((i) => i.title === product?.title)?.imageUrl || null;
                              return (
                                <li key={pId} className='text-sm flex items-center justify-between p-2'>
                                  <div className='flex items-center gap-6'>
                                    <FallbackImage
                                      src={productImage}
                                      alt={product?.title || pId}
                                      imgClassName='w-[100px] h-[100px] object-contain rounded-md'
                                    />
                                    <span className='proxima-nova-bold'>{product?.title || pId}</span>
                                  </div>
                                  <button
                                    type='button'
                                    onClick={() => {
                                      setProductToRemove(product || { id: pId, title: pId });
                                      setShowRemoveDialog(true);
                                    }}
                                    className='text-roland-blue hover:text-red-500 transition-colors p-1'
                                    title={t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.REMOVE', 'Remove')}
                                  >
                                    <CloseIcon />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className='rounded-2xl border-black/50 border overflow-hidden flex items-center gap-4 p-4'>
            <span className='flex-shrink-0'>
              <InfoIcon />
            </span>
            <span
              className='flex-grow'
              /* eslint-disable-next-line react/no-danger */
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(t('RVS.COMPONENT.LOYALTY_PROGRAM.HTML.NO_REWARDS', 'Stay tuned for information on any earned benefits. Thanks for your continued support.')),
              }}
            />
          </div>
        )}

        {/* Billing History Dialog */}
        <ModalRoot
          open={showBilling}
          onOpenChange={(e) => setShowBilling(e.open)}
        >
          <ModalBackdrop className='!bg-black/80' />
          <ModalPositioner className='!p-0'>
            <ModalContent className='!max-w-4xl !flex !flex-col !overflow-hidden !max-h-[calc(100dvh-7.5rem)] !rounded-3xl !shadow-2xl md:p-8 relative mx-2 text-sm'>
              <div className='relative'>
                <button
                  type='button'
                  onClick={() => setShowBilling(false)}
                  className='absolute top-5 right-4 md:top-0 md:right-0 p-1 md:p-0 hover:bg-white/10 rounded-full transition-colors text-white'
                  aria-label='Close Dialog'
                >
                  <CloseIcon />
                </button>
                <div className='px-6 pt-6'>
                  <ModalTitle className='!text-2xl !text-black'>{t('RVS.COMPONENT.BILLING_HISTORY.TITLE')}</ModalTitle>
                </div>
              </div>
              <div className='flex-1 min-h-0 px-6 py-6 overflow-y-auto'>
                <div>
                  {/* Mobile View: List */}
                  <ul className='space-y-2 overflow-y-auto md:hidden'>
                    {
                      billingHistory.map((entry, i) => (
                        <li className='' key={entry.transactionId || i}>
                          <hr className='py-1 border-black/10' />
                          <dl className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1'>
                            <dt className='font-bold'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_DATE', 'Date')}</dt>
                            <dd className=''>{entry.date ? new Date(entry.date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}</dd>

                            <dt className='font-bold'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_TYPE', 'Type')}</dt>
                            <dd className='capitalize'>{entry.orderType || '—'}</dd>

                            <dt className='font-bold'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_ITEM', 'Item')}</dt>
                            <dd className=''>{entry.entitlement || entry.description || '—'}</dd>

                            <dt className='font-bold'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_PRICE', 'Price')}</dt>
                            <dd className=''>{entry.amount != null ? `$${entry.amount} ${entry.currency || 'USD'}` : '—'}</dd>
                          </dl>
                        </li>
                      ))
                    }
                  </ul>

                  {/* Desktop View: Table */}
                  <div className='hidden md:block overflow-x-auto'>
                    <table className='w-full border-collapse table-fixed'>
                      <thead>
                        <tr className='border-b border-black/10'>
                          <th className='text-left py-3 pr-6 font-bold text-xs tracking-wider'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_DATE', 'Date')}</th>
                          <th className='text-left py-3 pr-6 font-bold text-xs tracking-wider'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_TYPE', 'Type')}</th>
                          <th className='text-left py-3 pr-6 font-bold text-xs tracking-wider'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_ITEM', 'Item')}</th>
                          <th className='text-left py-3 pr-6 font-bold text-xs tracking-wider'>{t('RVS.COMPONENT.BILLING_HISTORY.COL_PRICE', 'Price')}</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-black/5'>
                        {billingHistory.map((entry, i) => (
                          <tr key={entry.transactionId || i} className='hover:bg-gray-50 transition-colors'>
                            <td className='py-2 pr-6 whitespace-nowrap'>
                              {entry.date ? new Date(entry.date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                            </td>
                            <td className='py-2 pr-6 capitalize whitespace-nowrap'>
                              {entry.orderType || '—'}
                            </td>
                            <td className='py-2 pr-6'>
                              {entry.entitlement || entry.description || '—'}
                            </td>
                            <td className='py-2 pr-6 whitespace-nowrap'>
                              {entry.amount != null ? `$${entry.amount} ${entry.currency || 'USD'}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </ModalContent>
          </ModalPositioner>
        </ModalRoot>

        {selectedSub && (
          <SubscriptionCancellationDialog
            sub={selectedSub}
            isOpen={showCancelDialog}
            onOpenChange={(e) => setShowCancelDialog(e.open)}
            idToken={idToken}
            onCancelled={refetchAll}
          />
        )}

        <RewardSelectionDialog
          isOpen={showClaimDialog}
          onOpenChange={(e) => setShowClaimDialog(e.open)}
          allProducts={allProducts}
          productImages={productImages}
          userRuleStatus={selectedRule}
          idToken={idToken}
          username={username}
          onClaimed={refetchAll}
        />

        <RewardRemovalDialog
          isOpen={showRemoveDialog}
          onOpenChange={(e) => setShowRemoveDialog(e.open)}
          product={productToRemove}
          idToken={idToken}
          username={username}
          onRemoved={refetchAll}
        />
      </div>
    </>
  );
}
